import type { SupportedLocale } from '@erp/shared';
export const pdfFieldCheckTranslations = {
  de: {
    title: 'Zusatzfelder prüfen',
    hint: '„Im PDF anzeigen“ genügt für die automatische Ausgabe. Hier sehen Sie den gespeicherten Stand dieser Vorschau; der Designer ist optional.',
    help: 'Hilfe zur Felddiagnose',
    empty: 'Keine lesbaren Zusatzfelder vorhanden.',
    item: 'Position {{position}}',
    value: 'Verwendeter Wert',
    unavailable: 'Kein auslesbarer Belegwert',
    emptyValue: 'Leer',
    origin: 'Wertherkunft',
    shown: 'Wird angezeigt',
    hidden: 'Wird nicht angezeigt',
    masterChanged:
      'Der aktuelle Kundenwert unterscheidet sich vom gespeicherten Belegstand. Der Beleg wurde nicht aktualisiert.',
    settings: 'Feldeinstellung',
    storedValue: 'Belegwert ansehen',
    compare: 'Kundenwerte vergleichen',
    design: 'Platzierung im Designer',
    identity: 'Feldidentität',
    stale:
      'Die Vorschau ist veraltet. Aktualisieren Sie sie gezielt. Ein Download enthält weiterhin genau den angezeigten Stand.',
    stateError: 'Der aktuelle Stand konnte nicht geprüft werden. Bitte erneut aktualisieren.',
    noSample: 'Kein Beleg für die Vorschau vorhanden.',
    sources: {
      CUSTOMER: 'Kunde',
      ADDRESS: 'Lieferadresse',
      DOCUMENT: 'Belegkopf',
      PRODUCT: 'Artikel',
      DOCUMENT_ITEM: 'Position',
    },
    origins: {
      MASTER_DATA: 'Gespeicherter Stammdatenstand',
      DOCUMENT_VALUE: 'Gespeicherter Belegwert',
      SUPPLIER_SNAPSHOT: 'Gespeicherter Lieferantenstand',
      INHERITED: 'Vom Vorgängerbeleg übernommen',
      MANUAL_OVERRIDE: 'Bewusste Änderung im Beleg',
      BACKFILL_CURRENT_MASTER: 'Bewusst aus Stammdaten ergänzt',
      MISSING_SNAPSHOT: 'Kein zugeordneter Belegstand',
    },
    reasons: {
      VISIBLE: 'PDF-Freigabe, Wert und Platzierung erlauben die Ausgabe.',
      EMPTY: 'Wert im Beleg leer; leere Werte werden ausgeblendet.',
      MISSING_SNAPSHOT:
        'Wertübernahme oder Feldzuordnung fehlt im gespeicherten Belegstand. Aktuelle Stammdaten werden nicht automatisch eingesetzt.',
      HIDDEN: 'Das Feld ist im Layout ausgeblendet.',
      BLOCK_HIDDEN: 'Der übergeordnete Bereich wird in dieser Ausgabe nicht angezeigt.',
      NOT_RELEASED: '„Im PDF anzeigen“ ist im maßgeblichen Belegstand nicht freigegeben.',
      NOT_APPLICABLE: 'Für diese Belegart oder diesen Bereich nicht anwendbar.',
      PLACEMENT_MISSING: 'Der individuell zugeordnete Layoutbereich existiert nicht mehr.',
      CONFIDENTIAL: 'Vertrauliche Felder sind für das Kunden-PDF gesperrt.',
      INACTIVE: 'Die Felddefinition ist nicht aktiv.',
    },
    placement: {
      AUTO: 'Automatische Platzierung',
      INDIVIDUAL: 'Individuelle Platzierung',
    },
  },
  en: {
    title: 'Check custom fields',
    hint: '“Show in PDF” is sufficient for automatic output. This view explains the saved state of this preview; the designer is optional.',
    help: 'Help with field diagnostics',
    empty: 'No readable custom fields available.',
    item: 'Item {{position}}',
    value: 'Value used',
    unavailable: 'No readable document value',
    emptyValue: 'Empty',
    origin: 'Value origin',
    shown: 'Displayed',
    hidden: 'Not displayed',
    masterChanged:
      'The current customer value differs from the saved document snapshot. The document has not been updated.',
    settings: 'Field setting',
    storedValue: 'View document value',
    compare: 'Compare customer values',
    design: 'Placement in designer',
    identity: 'Field identity',
    stale:
      'This preview is outdated. Refresh it explicitly. Downloads still contain exactly the displayed version.',
    stateError: 'The current state could not be checked. Please refresh again.',
    noSample: 'No document available for preview.',
    sources: {
      CUSTOMER: 'Customer',
      ADDRESS: 'Delivery address',
      DOCUMENT: 'Document header',
      PRODUCT: 'Product',
      DOCUMENT_ITEM: 'Item',
    },
    origins: {
      MASTER_DATA: 'Saved master data snapshot',
      DOCUMENT_VALUE: 'Saved document value',
      SUPPLIER_SNAPSHOT: 'Saved supplier snapshot',
      INHERITED: 'Inherited from preceding document',
      MANUAL_OVERRIDE: 'Explicit document override',
      BACKFILL_CURRENT_MASTER: 'Explicit master data backfill',
      MISSING_SNAPSHOT: 'No mapped document snapshot',
    },
    reasons: {
      VISIBLE: 'PDF release, value and placement allow output.',
      EMPTY: 'The document value is empty and empty values are hidden.',
      MISSING_SNAPSHOT:
        'Value transfer or field mapping is missing in the saved document. Current master data is not inserted automatically.',
      HIDDEN: 'The field is hidden in the layout.',
      BLOCK_HIDDEN: 'The parent area is not displayed in this output.',
      NOT_RELEASED: '“Show in PDF” is not enabled in the applicable document snapshot.',
      NOT_APPLICABLE: 'Not applicable to this document type or area.',
      PLACEMENT_MISSING: 'The individually assigned layout area no longer exists.',
      CONFIDENTIAL: 'Confidential fields are blocked from customer PDFs.',
      INACTIVE: 'The field definition is not active.',
    },
    placement: {
      AUTO: 'Automatic placement',
      INDIVIDUAL: 'Individual placement',
    },
  },
  fr: {
    title: 'Vérifier les champs personnalisés',
    hint: '« Afficher dans le PDF » suffit pour la sortie automatique. Cette vue explique les données enregistrées de cet aperçu ; le concepteur est facultatif.',
    help: 'Aide au diagnostic des champs',
    empty: 'Aucun champ personnalisé lisible.',
    item: 'Ligne {{position}}',
    value: 'Valeur utilisée',
    unavailable: 'Aucune valeur du document lisible',
    emptyValue: 'Vide',
    origin: 'Origine de la valeur',
    shown: 'Affiché',
    hidden: 'Non affiché',
    masterChanged:
      'La valeur actuelle du client diffère de celle enregistrée dans le document. Le document n’a pas été actualisé.',
    settings: 'Paramètre du champ',
    storedValue: 'Voir la valeur du document',
    compare: 'Comparer les valeurs client',
    design: 'Placement dans le concepteur',
    identity: 'Identité du champ',
    stale:
      'Cet aperçu est obsolète. Actualisez-le explicitement. Le téléchargement contient toujours exactement la version affichée.',
    stateError: 'Impossible de vérifier l’état actuel. Veuillez actualiser à nouveau.',
    noSample: 'Aucun document disponible pour l’aperçu.',
    sources: {
      CUSTOMER: 'Client',
      ADDRESS: 'Adresse de livraison',
      DOCUMENT: 'En-tête du document',
      PRODUCT: 'Article',
      DOCUMENT_ITEM: 'Ligne',
    },
    origins: {
      MASTER_DATA: 'Données de base enregistrées',
      DOCUMENT_VALUE: 'Valeur enregistrée du document',
      SUPPLIER_SNAPSHOT: 'État enregistré du fournisseur',
      INHERITED: 'Repris du document précédent',
      MANUAL_OVERRIDE: 'Modification explicite du document',
      BACKFILL_CURRENT_MASTER: 'Ajout explicite depuis les données de base',
      MISSING_SNAPSHOT: 'Aucune donnée enregistrée associée',
    },
    reasons: {
      VISIBLE: 'L’autorisation PDF, la valeur et le placement permettent l’affichage.',
      EMPTY: 'La valeur du document est vide et les valeurs vides sont masquées.',
      MISSING_SNAPSHOT:
        'Le transfert de valeur ou l’association du champ manque dans le document enregistré. Les données de base actuelles ne sont pas insérées automatiquement.',
      HIDDEN: 'Le champ est masqué dans la mise en page.',
      BLOCK_HIDDEN: 'La zone parente n’est pas affichée dans cette sortie.',
      NOT_RELEASED:
        '« Afficher dans le PDF » n’est pas autorisé dans les données du document utilisées.',
      NOT_APPLICABLE: 'Non applicable à ce type de document ou à cette zone.',
      PLACEMENT_MISSING: 'La zone de mise en page attribuée individuellement n’existe plus.',
      CONFIDENTIAL: 'Les champs confidentiels sont exclus des PDF destinés aux clients.',
      INACTIVE: 'La définition du champ n’est pas active.',
    },
    placement: {
      AUTO: 'Placement automatique',
      INDIVIDUAL: 'Placement individuel',
    },
  },
  es: {
    title: 'Comprobar campos adicionales',
    hint: '« Mostrar en PDF » basta para la salida automática. Esta vista explica los datos guardados de esta vista previa; el diseñador es opcional.',
    help: 'Ayuda del diagnóstico de campos',
    empty: 'No hay campos adicionales legibles.',
    item: 'Posición {{position}}',
    value: 'Valor utilizado',
    unavailable: 'Sin valor legible del documento',
    emptyValue: 'Vacío',
    origin: 'Origen del valor',
    shown: 'Se muestra',
    hidden: 'No se muestra',
    masterChanged:
      'El valor actual del cliente difiere del guardado en el documento. El documento no se ha actualizado.',
    settings: 'Ajuste del campo',
    storedValue: 'Ver valor del documento',
    compare: 'Comparar valores del cliente',
    design: 'Ubicación en el diseñador',
    identity: 'Identidad del campo',
    stale:
      'Esta vista previa está desactualizada. Actualícela expresamente. La descarga sigue conteniendo exactamente la versión mostrada.',
    stateError: 'No se pudo comprobar el estado actual. Vuelva a actualizar.',
    noSample: 'No hay documento disponible para la vista previa.',
    sources: {
      CUSTOMER: 'Cliente',
      ADDRESS: 'Dirección de entrega',
      DOCUMENT: 'Cabecera del documento',
      PRODUCT: 'Artículo',
      DOCUMENT_ITEM: 'Posición',
    },
    origins: {
      MASTER_DATA: 'Datos maestros guardados',
      DOCUMENT_VALUE: 'Valor guardado del documento',
      SUPPLIER_SNAPSHOT: 'Estado guardado del proveedor',
      INHERITED: 'Heredado del documento anterior',
      MANUAL_OVERRIDE: 'Cambio explícito en el documento',
      BACKFILL_CURRENT_MASTER: 'Incorporación explícita de datos maestros',
      MISSING_SNAPSHOT: 'Sin datos guardados asociados',
    },
    reasons: {
      VISIBLE: 'La autorización PDF, el valor y la ubicación permiten la salida.',
      EMPTY: 'El valor del documento está vacío y los valores vacíos se ocultan.',
      MISSING_SNAPSHOT:
        'Falta la transferencia del valor o la asignación del campo en el documento guardado. Los datos maestros actuales no se insertan automáticamente.',
      HIDDEN: 'El campo está oculto en el diseño.',
      BLOCK_HIDDEN: 'La sección superior no se muestra en esta salida.',
      NOT_RELEASED: '« Mostrar en PDF » no está autorizado en los datos guardados utilizados.',
      NOT_APPLICABLE: 'No es aplicable a este tipo de documento o sección.',
      PLACEMENT_MISSING: 'La sección del diseño asignada individualmente ya no existe.',
      CONFIDENTIAL: 'Los campos confidenciales están excluidos del PDF del cliente.',
      INACTIVE: 'La definición del campo no está activa.',
    },
    placement: {
      AUTO: 'Ubicación automática',
      INDIVIDUAL: 'Ubicación individual',
    },
  },
  tr: {
    title: 'Ek alanları kontrol et',
    hint: 'Otomatik çıktı için “PDF’de göster” yeterlidir. Bu görünüm, önizlemenin kayıtlı verilerini açıklar; tasarımcı isteğe bağlıdır.',
    help: 'Alan tanılama yardımı',
    empty: 'Okunabilir ek alan bulunmuyor.',
    item: 'Kalem {{position}}',
    value: 'Kullanılan değer',
    unavailable: 'Okunabilir belge değeri yok',
    emptyValue: 'Boş',
    origin: 'Değerin kaynağı',
    shown: 'Gösteriliyor',
    hidden: 'Gösterilmiyor',
    masterChanged: 'Güncel müşteri değeri, belgede kayıtlı değerden farklı. Belge güncellenmedi.',
    settings: 'Alan ayarı',
    storedValue: 'Belge değerini görüntüle',
    compare: 'Müşteri değerlerini karşılaştır',
    design: 'Tasarımcıdaki yerleşim',
    identity: 'Alan kimliği',
    stale:
      'Bu önizleme güncel değil. Bilinçli olarak yenileyin. İndirme, görüntülenen sürümün aynısını içerir.',
    stateError: 'Güncel durum kontrol edilemedi. Lütfen tekrar yenileyin.',
    noSample: 'Önizleme için belge bulunmuyor.',
    sources: {
      CUSTOMER: 'Müşteri',
      ADDRESS: 'Teslimat adresi',
      DOCUMENT: 'Belge başlığı',
      PRODUCT: 'Ürün',
      DOCUMENT_ITEM: 'Kalem',
    },
    origins: {
      MASTER_DATA: 'Kayıtlı ana veri anlık görüntüsü',
      DOCUMENT_VALUE: 'Kayıtlı belge değeri',
      SUPPLIER_SNAPSHOT: 'Kayıtlı tedarikçi bilgileri',
      INHERITED: 'Önceki belgeden devralınmış',
      MANUAL_OVERRIDE: 'Belgede bilinçli değişiklik',
      BACKFILL_CURRENT_MASTER: 'Ana verilerden bilinçli tamamlama',
      MISSING_SNAPSHOT: 'Eşleşen kayıtlı belge verisi yok',
    },
    reasons: {
      VISIBLE: 'PDF izni, değer ve yerleşim çıktıya izin veriyor.',
      EMPTY: 'Belgedeki değer boş ve boş değerler gizleniyor.',
      MISSING_SNAPSHOT:
        'Kayıtlı belgede değer aktarımı veya alan eşlemesi eksik. Güncel ana veriler otomatik eklenmez.',
      HIDDEN: 'Alan düzende gizlenmiş.',
      BLOCK_HIDDEN: 'Üst bölüm bu çıktıda gösterilmiyor.',
      NOT_RELEASED: 'Kullanılan kayıtlı belge verisinde “PDF’de göster” izni yok.',
      NOT_APPLICABLE: 'Bu belge türü veya bölüm için geçerli değil.',
      PLACEMENT_MISSING: 'Özel olarak atanan düzen bölümü artık mevcut değil.',
      CONFIDENTIAL: 'Gizli alanlar müşteri PDF’sine dahil edilmez.',
      INACTIVE: 'Alan tanımı etkin değil.',
    },
    placement: {
      AUTO: 'Otomatik yerleşim',
      INDIVIDUAL: 'Özel yerleşim',
    },
  },
} as const satisfies Record<SupportedLocale, object>;
